import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { fetchLeaseAnalysis } from '../services/leaseAnalysisService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const formatPct = (rate) => {
  if (rate === null || rate === undefined) return '—';
  return `${(Number(rate) * 100).toFixed(1)}%`;
};

const formatYears = (years) => {
  if (years === null || years === undefined) return '—';
  return `${Number(years).toFixed(2)} yrs`;
};

const pctTooltip = (context) => {
  const value = context.parsed.y;
  if (value === null || value === undefined) return `${context.dataset.label}: —`;
  return `${context.dataset.label}: ${value.toFixed(1)}%`;
};

/**
 * Centered rolling mean, used only to draw a readable churn trend.
 *
 * Deep in the curve each month holds few tenants, so a single move-out swings
 * the rate several points and the raw series alternates between spikes and
 * zeros. Smoothing the *display* is standard; the underlying survival math is
 * never smoothed, so nothing downstream of it changes.
 */
const rollingMean = (values, window) => {
  const half = Math.floor(window / 2);
  return values.map((_, i) => {
    let sum = 0;
    let count = 0;
    for (let j = i - half; j <= i + half; j += 1) {
      if (j >= 0 && j < values.length && values[j] != null) {
        sum += values[j];
        count += 1;
      }
    }
    return count ? sum / count : null;
  });
};

const formatTurns = (rate) => {
  if (rate === null || rate === undefined) return '—';
  return Number(rate).toFixed(3);
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white border rounded-lg p-4 min-w-[140px] flex-1">
    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</div>
    <div className="text-2xl font-semibold text-gray-900">{value}</div>
    {sub ? <div className="text-xs text-gray-500 mt-1">{sub}</div> : null}
  </div>
);

const ChartCard = ({ title, subtitle, children }) => (
  <div className="bg-white border rounded-lg p-4">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    {subtitle ? <p className="text-sm text-gray-500 mt-1 mb-4">{subtitle}</p> : <div className="mb-4" />}
    <div className="h-72">{children}</div>
  </div>
);

function LeaseAnalysis() {
  const [data, setData] = useState(null);
  const [owner, setOwner] = useState('');
  const [includeTruncated, setIncludeTruncated] = useState(true);
  const [monthHorizon, setMonthHorizon] = useState(60);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');

    fetchLeaseAnalysis({
      owner: owner || undefined,
      includeTruncated,
      signal: controller.signal,
    })
      .then((payload) => setData(payload))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Failed to load lease analysis');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [owner, includeTruncated]);

  const summary = data?.summary;
  const owners = data?.owners || [];
  const dataQuality = data?.data_quality;
  const survivalSummary = data?.monthly_survival?.summary;
  const retentionChart = useMemo(() => {
    const observed = data?.year_retention || [];
    // The observation window is only a few years long, so the directly
    // observed bars run out well before the survival curve does. Plot both on
    // one axis: bars where a full cohort exists, the curve everywhere it holds
    // a healthy risk set.
    const modeled = (data?.monthly_survival?.cumulative_at_year || []).filter(
      (r) => r.reliable && r.rate != null
    );
    const maxYear = Math.max(
      modeled.length ? modeled[modeled.length - 1].years : 0,
      ...observed.filter((r) => r.rate != null).map((r) => r.years),
      1
    );
    const years = Array.from({ length: maxYear }, (_, i) => i + 1);
    const observedBy = new Map(observed.map((r) => [r.years, r]));
    const modeledBy = new Map(modeled.map((r) => [r.years, r]));

    return {
      data: {
        labels: years.map((y) => `${y} yr${y === 1 ? '' : 's'}`),
        datasets: [
          {
            label: 'Recorded from move-in',
            data: years.map((y) => {
              const row = observedBy.get(y);
              return row && row.rate != null ? row.rate * 100 : null;
            }),
            backgroundColor: 'rgba(37, 99, 235, 0.75)',
            borderColor: 'rgb(37, 99, 235)',
            borderWidth: 1,
            borderRadius: 4,
            order: 2,
          },
          {
            // Floating bar rendered narrow so it reads as a 95% error bar.
            label: '95% interval',
            data: years.map((y) => {
              const row = observedBy.get(y);
              return row && row.ci_low != null
                ? [row.ci_low * 100, row.ci_high * 100]
                : null;
            }),
            backgroundColor: 'rgba(30, 58, 138, 0.85)',
            borderWidth: 0,
            barPercentage: 0.06,
            categoryPercentage: 0.9,
            grouped: false,
            order: 1,
          },
          {
            type: 'line',
            label: 'Estimate using all observed history',
            data: years.map((y) => {
              const row = modeledBy.get(y);
              return row ? row.rate * 100 : null;
            }),
            borderColor: 'rgb(217, 119, 6)',
            backgroundColor: 'rgb(217, 119, 6)',
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.25,
            fill: false,
            spanGaps: false,
            order: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            filter: (ctx) => ctx.datasetIndex !== 1,
            callbacks: {
              label: pctTooltip,
              afterLabel: (ctx) => {
                const year = years[ctx.dataIndex];
                if (ctx.datasetIndex === 2) {
                  const row = modeledBy.get(year);
                  return row ? `${row.at_risk} tenancies contributing at month ${row.month}` : '';
                }
                const row = observedBy.get(year);
                if (!row || row.rate == null) return '';
                const lines = [`${row.retained} of ${row.eligible} tenancies with enough history`];
                if (row.ci_low != null) {
                  lines.push(
                    `Likely range: ${formatPct(row.ci_low)} – ${formatPct(row.ci_high)}`
                  );
                }
                return lines;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: 100,
            ticks: { callback: (v) => `${v}%` },
            title: { display: true, text: '% still in place' },
          },
          x: {
            title: { display: true, text: 'Tenancy age reached' },
          },
        },
      },
    };
  }, [data]);

  const survivalChart = useMemo(() => {
    // Directly observed transitions run out of denominator fast — year 3 to 4
    // comes down to a handful of tenants. The survival curve estimates the same
    // conditional probability from every tenant at risk at those ages, so it
    // leads here and the observed points ride on top as a spot check.
    const modeled = (data?.monthly_survival?.annualized_by_year || []).filter(
      (r) => r.reliable && r.rate != null
    );
    const observed = data?.survival_transitions || [];
    const maxYear = Math.max(
      modeled.length ? modeled[modeled.length - 1].year : 0,
      ...observed.filter((r) => r.rate != null).map((r) => r.to_year),
      1
    );
    const years = Array.from({ length: maxYear }, (_, i) => i + 1);
    const modeledBy = new Map(modeled.map((r) => [r.year, r]));
    const observedBy = new Map(observed.map((r) => [r.to_year, r]));

    return {
      data: {
        labels: years.map((y) => `${y - 1}→${y}`),
        datasets: [
          {
            label: 'Estimate using all observed history',
            data: years.map((y) => {
              const row = modeledBy.get(y);
              return row ? row.rate * 100 : null;
            }),
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            pointBackgroundColor: 'rgb(16, 185, 129)',
            pointRadius: 4,
            tension: 0.25,
            fill: false,
            spanGaps: false,
          },
          {
            label: 'Recorded from move-in',
            data: years.map((y) => {
              const row = observedBy.get(y);
              return row && row.rate != null ? row.rate * 100 : null;
            }),
            borderColor: 'rgb(37, 99, 235)',
            backgroundColor: 'rgb(37, 99, 235)',
            pointStyle: 'rectRot',
            pointRadius: 6,
            showLine: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            callbacks: {
              label: pctTooltip,
              afterLabel: (ctx) => {
                const year = years[ctx.dataIndex];
                if (ctx.datasetIndex === 0) {
                  const row = modeledBy.get(year);
                  return row ? `At least ${row.min_at_risk} tenancies contributing in this year` : '';
                }
                const row = observedBy.get(year);
                if (!row || row.rate == null) return '';
                return [
                  `${row.survived} of ${row.at_risk} tenancies stayed`,
                  `Likely range: ${formatPct(row.ci_low)} – ${formatPct(row.ci_high)}`,
                ];
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: 100,
            ticks: { callback: (v) => `${v}%` },
            title: { display: true, text: '% staying through the year' },
          },
          x: {
            title: { display: true, text: 'Year of tenancy' },
          },
        },
      },
    };
  }, [data]);

  const monthlyCumulativeChart = useMemo(() => {
    const all = data?.monthly_survival?.cumulative || [];
    const extrapolated = data?.monthly_survival?.extrapolated_cumulative || [];
    // Observed KM stops where the risk set thins. Fitted continuation (if any)
    // continues the same S(t) with the renewal-cycle tail model.
    const horizon = data?.monthly_survival?.summary?.reliable_horizon_months ?? 0;
    const rows = all.filter(
      (r) => r.survival_rate != null && r.month <= Math.min(horizon, monthHorizon),
    );
    const fittedRows = extrapolated.filter(
      (r) => r.survival_rate != null && r.month <= monthHorizon && r.month > horizon,
    );
    const labels = [
      ...rows.map((r) => String(r.month)),
      ...fittedRows.map((r) => String(r.month)),
    ];
    const observedSeries = [
      ...rows.map((r) => (r.survival_rate == null ? null : r.survival_rate * 100)),
      ...fittedRows.map(() => null),
    ];
    const fittedSeries = [
      ...rows.map((r, i) =>
        i === rows.length - 1 && r.survival_rate != null ? r.survival_rate * 100 : null,
      ),
      ...fittedRows.map((r) => r.survival_rate * 100),
    ];
    return {
      data: {
        labels,
        datasets: [
          {
            label: 'Recorded-history estimate',
            data: observedSeries,
            borderColor: 'rgb(79, 70, 229)',
            backgroundColor: 'rgba(79, 70, 229, 0.12)',
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.15,
            fill: false,
            spanGaps: false,
            order: 0,
          },
          {
            label: 'Projected later years',
            data: fittedSeries,
            borderColor: 'rgb(217, 119, 6)',
            borderDash: [6, 4],
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointHitRadius: 12,
            tension: 0.15,
            fill: false,
            spanGaps: false,
            order: 1,
          },
          {
            label: 'CI upper',
            data: [
              ...rows.map((r) => (r.ci_high == null ? null : r.ci_high * 100)),
              ...fittedRows.map(() => null),
            ],
            borderColor: 'rgba(79, 70, 229, 0.2)',
            borderWidth: 1,
            pointRadius: 0,
            tension: 0.15,
            fill: '+1',
            backgroundColor: 'rgba(79, 70, 229, 0.13)',
            spanGaps: false,
            order: 2,
          },
          {
            label: 'CI lower',
            data: [
              ...rows.map((r) => (r.ci_low == null ? null : r.ci_low * 100)),
              ...fittedRows.map(() => null),
            ],
            borderColor: 'rgba(79, 70, 229, 0.2)',
            borderWidth: 1,
            pointRadius: 0,
            tension: 0.15,
            fill: false,
            spanGaps: false,
            order: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: fittedRows.length > 0,
            position: 'bottom',
            labels: { filter: (item) => item.datasetIndex < 2 },
          },
          tooltip: {
            filter: (ctx) => ctx.datasetIndex === 0 || ctx.datasetIndex === 1,
            callbacks: {
              title: (items) => `Month ${items?.[0]?.label}`,
              label: pctTooltip,
              afterBody: (items) => {
                const idx = items?.[0]?.dataIndex;
                if (idx == null) return [];
                if (idx < rows.length) {
                  const row = rows[idx];
                  if (!row || row.ci_low == null) return [];
                  return [
                    `Likely range: ${formatPct(row.ci_low)} – ${formatPct(row.ci_high)}`,
                    `${row.at_risk} tenancies contributing`,
                  ];
                }
                return ['Projected from the recorded renewal pattern'];
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: 100,
            ticks: { callback: (v) => `${v}%` },
            title: { display: true, text: '% still in place' },
          },
          x: {
            title: { display: true, text: 'Month of tenancy' },
            ticks: { maxTicksLimit: 20 },
          },
        },
      },
    };
  }, [data, monthHorizon]);

  const monthlyChurnChart = useMemo(() => {
    const horizon = Math.min(
      data?.monthly_survival?.summary?.reliable_horizon_months ?? 0,
      monthHorizon
    );
    const rows = (data?.monthly_survival?.transitions || []).filter(
      (r) => r.to_month <= horizon
    );
    // The fitted line is drawn over the recorded months too, and averaged over
    // the same 7-month window as the actual line, so the two can be compared
    // directly. Smoothing runs over the whole fitted series before it is cut
    // to the selected horizon, so the cut does not change the edge values.
    const fittedAll = data?.monthly_survival?.fitted_churn || [];
    const fittedSmoothed = rollingMean(
      fittedAll.map((r) => (r.churn_rate == null ? null : r.churn_rate * 100)),
      7
    );
    const fittedByMonth = new Map(
      fittedAll.map((r, i) => [r.to_month, fittedSmoothed[i]])
    );
    const lastFitted = fittedAll.length ? fittedAll[fittedAll.length - 1].to_month : 0;
    const lastMonth = Math.max(horizon, Math.min(lastFitted, monthHorizon));
    const months = Array.from({ length: lastMonth }, (_, i) => i + 1);

    const rawObs = rows.map((r) => (r.churn_rate == null ? null : r.churn_rate * 100));
    const smoothedObs = rollingMean(rawObs, 7);
    const padObserved = (values) => months.map((_, i) => (i < rows.length ? values[i] : null));
    const raw = padObserved(rawObs);
    const smoothed = padObserved(smoothedObs);
    const fittedSeries = months.map((m) => fittedByMonth.get(m) ?? null);
    const labels = months.map(String);
    return {
      data: {
        labels,
        datasets: [
          {
            label: 'Actual, 7-month average',
            data: smoothed,
            borderColor: 'rgb(190, 18, 60)',
            backgroundColor: 'rgba(225, 29, 72, 0.12)',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointHitRadius: 12,
            tension: 0.3,
            fill: true,
            spanGaps: false,
            order: 0,
          },
          {
            label: 'Fitted, 7-month average',
            data: fittedSeries,
            borderColor: 'rgb(217, 119, 6)',
            borderDash: [6, 4],
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointHitRadius: 12,
            tension: 0.2,
            fill: false,
            spanGaps: false,
            order: 1,
          },
          {
            label: 'Actual, monthly',
            data: raw,
            borderColor: 'rgba(225, 29, 72, 0.3)',
            borderWidth: 1,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointHitRadius: 12,
            tension: 0.1,
            fill: false,
            spanGaps: false,
            order: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              filter: (item) =>
                item.text !== 'Fitted, 7-month average' || fittedAll.length > 0,
            },
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            filter: (item) => item.parsed.y != null,
            callbacks: {
              title: (items) => `Month ${items?.[0]?.label}`,
              label: (context) => {
                const value = context.parsed.y;
                if (value == null) return null;
                if (context.datasetIndex === 0) return `Actual, 7-month average: ${value.toFixed(1)}%`;
                if (context.datasetIndex === 2) return `Actual that month: ${value.toFixed(1)}%`;
                return `Fitted, 7-month average: ${value.toFixed(1)}%`;
              },
              afterBody: (items) => {
                const idx = items?.[0]?.dataIndex;
                if (idx == null) return [];
                if (idx < rows.length) {
                  const row = rows[idx];
                  if (!row) return [];
                  return [
                    `${row.failed} move-outs`,
                    `${row.survived} stayed through the month`,
                    `${row.known_outcomes} tenancies with a known outcome`,
                    row.censored ? `${row.censored} partial observations excluded from the rate` : null,
                  ].filter(Boolean);
                }
                return ['Past the recorded months: fitted model only.'];
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: (v) => `${v}%` },
            title: { display: true, text: 'Move-out rate that month' },
          },
          x: {
            title: { display: true, text: 'Month of tenancy' },
            ticks: { maxTicksLimit: 20 },
          },
        },
      },
    };
  }, [data, monthHorizon]);

  const turnover = data?.turnover || null;

  const reservePathChart = useMemo(() => {
    const path = turnover?.portfolio?.turnover_path || [];
    // 1/E[tenure] is the true long-run rate. The path approaches it from above
    // early, then can undershoot inside a finite hold window because a unit
    // holding a very long tenant produces no turns inside that window.
    const steady = turnover?.steady_state?.turns_per_unit_year ?? null;
    return {
      data: {
        labels: path.map((_, i) => `Yr ${i + 1}`),
        datasets: [
          {
            label: 'Turns per unit',
            data: path,
            borderColor: 'rgb(217, 119, 6)',
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.25,
            fill: true,
            order: 0,
          },
          {
            label: 'Long-run average (1 ÷ tenant lifetime)',
            data: path.map(() => steady),
            borderColor: 'rgba(107, 114, 128, 0.8)',
            borderDash: [5, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            fill: false,
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (context) =>
                `${context.dataset.label}: ${Number(context.parsed.y).toFixed(3)} turns/unit`,
              afterBody: (items) => {
                const v = path[items?.[0]?.dataIndex];
                if (v == null) return [];
                return [`One turn every ${(1 / v).toFixed(1)} yrs at this rate`];
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Turnovers per unit' },
          },
          x: { title: { display: true, text: 'Year from today' } },
        },
      },
    };
  }, [turnover]);

  const distributionChart = useMemo(() => {
    const rows = data?.tenure_distribution || [];
    return {
      data: {
        labels: rows.map((r) => r.bucket),
        datasets: [
          {
            label: 'Estimated final length',
            data: rows.map((r) => (r.modeled_pct == null ? null : r.modeled_pct * 100)),
            backgroundColor: 'rgba(99, 102, 241, 0.75)',
            borderColor: 'rgb(99, 102, 241)',
            borderWidth: 1,
            borderRadius: 4,
          },
          {
            label: 'Time recorded so far',
            data: rows.map((r) => (r.observed_pct == null ? null : r.observed_pct * 100)),
            backgroundColor: 'rgba(148, 163, 184, 0.55)',
            borderColor: 'rgb(100, 116, 139)',
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            callbacks: {
              label: pctTooltip,
              afterLabel: (ctx) => {
                const row = rows[ctx.dataIndex];
                if (!row) return '';
                if (ctx.datasetIndex === 1) {
                  return `${row.observed_count} tenants at this length today`;
                }
                return 'Estimated from recorded and projected move-out rates';
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: (v) => `${v}%` },
            title: { display: true, text: '% of tenants' },
          },
        },
      },
    };
  }, [data]);

  const cohortChart = useMemo(() => {
    const rows = data?.cohorts_by_start_year || [];
    return {
      data: {
        labels: rows.map((r) => String(r.year)),
        datasets: [
          {
            type: 'bar',
            label: 'Cohort size',
            data: rows.map((r) => r.tenants),
            backgroundColor: 'rgba(148, 163, 184, 0.45)',
            borderColor: 'rgb(100, 116, 139)',
            borderWidth: 1,
            yAxisID: 'y',
            order: 4,
          },
          {
            type: 'line',
            label: '% staying at least 1 year',
            data: rows.map((r) => (r.retention_1yr == null ? null : r.retention_1yr * 100)),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgb(59, 130, 246)',
            pointRadius: 3,
            tension: 0.2,
            yAxisID: 'y1',
            order: 1,
            spanGaps: false,
          },
          {
            type: 'line',
            label: '% staying at least 2 years',
            data: rows.map((r) => (r.retention_2yr == null ? null : r.retention_2yr * 100)),
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgb(16, 185, 129)',
            pointRadius: 3,
            tension: 0.2,
            yAxisID: 'y1',
            order: 2,
            spanGaps: false,
          },
          {
            type: 'line',
            label: '% staying at least 3 years',
            data: rows.map((r) => (r.retention_3yr == null ? null : r.retention_3yr * 100)),
            borderColor: 'rgb(37, 99, 235)',
            backgroundColor: 'rgb(37, 99, 235)',
            pointRadius: 4,
            tension: 0.2,
            yAxisID: 'y1',
            order: 3,
            spanGaps: false,
          },
          {
            type: 'line',
            label: '% still current today',
            data: rows.map((r) => (r.still_current_rate == null ? null : r.still_current_rate * 100)),
            borderColor: 'rgb(234, 88, 12)',
            backgroundColor: 'rgb(234, 88, 12)',
            borderDash: [6, 4],
            pointRadius: 3,
            tension: 0.2,
            yAxisID: 'y1',
            order: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                if (ctx.parsed.y == null) return `${ctx.dataset.label}: not enough history yet`;
                if (ctx.dataset.yAxisID === 'y1') {
                  return `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`;
                }
                return `${ctx.dataset.label}: ${ctx.parsed.y}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            position: 'left',
            title: { display: true, text: 'Tenants' },
            ticks: { precision: 0 },
          },
          y1: {
            beginAtZero: true,
            suggestedMax: 100,
            position: 'right',
            grid: { drawOnChartArea: false },
            ticks: { callback: (v) => `${v}%` },
            title: { display: true, text: '% staying / still current' },
          },
        },
      },
    };
  }, [data]);

  const moveOutChart = useMemo(() => {
    const rows = data?.move_out_by_month || [];
    return {
      data: {
        labels: rows.map((r) => r.label),
        datasets: [
          {
            label: 'Move-out rate vs average month',
            data: rows.map((r) => r.hazard_index),
            backgroundColor: rows.map((r) =>
              (r.hazard_index ?? 100) >= 100
                ? 'rgba(244, 63, 94, 0.7)'
                : 'rgba(148, 163, 184, 0.6)'
            ),
            borderColor: 'rgb(225, 29, 72)',
            borderWidth: 1,
            borderRadius: 4,
            yAxisID: 'y',
          },
          {
            type: 'line',
            label: 'Scheduled lease ends',
            data: rows.map((r) => r.lease_end_count),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            pointRadius: 3,
            borderWidth: 2,
            tension: 0.25,
            fill: false,
            yAxisID: 'y1',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                if (ctx.datasetIndex === 1) {
                  return `Scheduled lease ends: ${ctx.parsed.y}`;
                }
                return `Move-out rate: ${ctx.parsed.y} (100 = average month)`;
              },
              afterBody: (items) => {
                const row = rows[items?.[0]?.dataIndex];
                if (!row) return [];
                return [
                  `${row.count} move-outs`,
                  `${row.exposure_months.toLocaleString()} occupied unit-months observed`,
                ];
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Move-out index (100 = average)' },
          },
          y1: {
            position: 'right',
            beginAtZero: true,
            ticks: { precision: 0 },
            grid: { drawOnChartArea: false },
            title: { display: true, text: 'Lease ends' },
          },
        },
      },
    };
  }, [data]);

  if (loading && !data) {
    return <div className="text-gray-600 py-8">Loading lease analysis…</div>;
  }

  if (error && !data) {
    return <div className="text-red-600 py-8">{error}</div>;
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Lease Analysis</h2>
          <p className="text-sm text-gray-500 mt-1">
            How long tenants stay, when move-outs happen, and how many turnovers to plan for
            {data?.as_of ? ` (as of ${data.as_of})` : ''}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label
            className="flex items-center gap-2 text-sm text-gray-700"
            title="Count the time we observed after purchase for tenants who were already living there. Their earlier, unobserved years are not treated as known history."
          >
            <input
              type="checkbox"
              checked={includeTruncated}
              onChange={(e) => setIncludeTruncated(e.target.checked)}
            />
            Include tenants inherited at purchase
          </label>
          <div className="flex items-center gap-2">
            <label htmlFor="lease-horizon" className="text-sm text-gray-600">
              Monthly charts
            </label>
            <select
              id="lease-horizon"
              className="border rounded px-3 py-2 text-sm"
              value={monthHorizon}
              onChange={(e) => setMonthHorizon(Number(e.target.value))}
            >
              <option value={36}>3 years</option>
              <option value={60}>5 years</option>
              <option value={120}>10 years</option>
              <option value={999}>Full history</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="lease-owner" className="text-sm text-gray-600">
              Owner
            </label>
            <select
              id="lease-owner"
              className="border rounded px-3 py-2 text-sm min-w-[180px]"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
            >
              <option value="">All owners</option>
              {owners.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {dataQuality?.exit_capture_start ? (
        <div className="bg-sky-50 border border-sky-200 text-sky-900 text-sm rounded-lg px-4 py-3">
          Reliable records begin <strong>{dataQuality.exit_capture_start}</strong>. For properties
          bought later, tracking begins on the purchase date.{' '}
          {dataQuality.fully_observed_tenants} of {dataQuality.observable_tenants} tenancies are
          recorded from move-in; {dataQuality.truncated_tenants} were already occupied and{' '}
          {dataQuality.include_truncated
              ? 'contribute only for the time we observed after tracking began.'
              : 'are left out of retention and lifetime estimates.'}
          {dataQuality.sentinel_start_tenants_excluded
            ? ` ${dataQuality.sentinel_start_tenants_excluded} placeholder 1970 starts are excluded from all tenure metrics.`
            : ''}
        </div>
      ) : null}

      {survivalSummary?.insufficient_data ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg px-4 py-3">
          {survivalSummary.insufficient_data} Survival-based charts are blank for this selection.
          Tenant counts, average tenure, and the milestone bars above are unaffected.
        </div>
      ) : null}

      <div className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg px-4 py-3 space-y-1">
          <div className="font-medium text-slate-900">How to read the retention numbers</div>
          <p>
          <span className="font-medium">Still in place</span> asks what share of new tenants reach a
          given age. <span className="font-medium">Stay one more year</span> asks, among tenants who
          already reached that age, what share remain for the following year. The second percentage
          is usually higher because it excludes tenants who left earlier.
        </p>
      </div>

      {error ? <div className="text-sm text-red-600">{error}</div> : null}

      <div className="flex flex-wrap gap-3">
        <StatCard
          label="Recorded tenancies"
          value={summary?.total_tenants ?? '—'}
          sub={`${summary?.current_tenants ?? 0} current · ${summary?.former_tenants ?? 0} former`}
        />
        <StatCard
          label="Average time recorded"
          value={formatYears(summary?.avg_tenure_years)}
          sub={`Median ${formatYears(summary?.median_tenure_years)}`}
        />
        <StatCard
          label="Average recorded move-out"
          value={formatYears(summary?.avg_completed_tenure_years)}
          sub="Completed stays only · long active stays are unfinished"
        />
        <StatCard
          label="Average current stay"
          value={formatYears(summary?.avg_current_tenure_years)}
          sub="Time in place so far for current tenants"
        />
        <StatCard
          label="Stayed at least 1 year"
          value={formatPct(data?.year_retention?.[0]?.rate)}
          sub={
            data?.year_retention?.[0]
              ? `${data.year_retention[0].retained} of ${data.year_retention[0].eligible} with a full year of history`
              : undefined
          }
        />
        <StatCard
          label="Estimated tenant lifetime"
          value={formatYears(survivalSummary?.mean_years)}
          sub={
            survivalSummary?.insufficient_data
              ? survivalSummary.insufficient_data
              : survivalSummary?.mean_months != null
                ? [
                    survivalSummary.mean_years_ci_low != null
                      ? `Likely range ${formatYears(survivalSummary.mean_years_ci_low)}–${formatYears(
                          survivalSummary.mean_years_ci_high
                        )}`
                      : null,
                    `Median ${survivalSummary.median_months ?? '—'} months · observed pattern through month ${
                      survivalSummary.reliable_horizon_months
                    }`,
                    survivalSummary.mean_is_extrapolated
                      ? `Future years follow the fitted lease-renewal pattern · 25-year undiscounted estimate`
                      : 'No future projection; this is a lower bound',
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : 'Estimated from the observed and projected move-out pattern'
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Tenants still in place"
          subtitle="Share of tenants who make it to each anniversary. Bars use tenancies with enough recorded history; the line also uses inherited tenants during the period we observed them."
        >
          <Bar data={retentionChart.data} options={retentionChart.options} />
        </ChartCard>

        <ChartCard
          title="Chance of staying one more year"
          subtitle="Among tenants who reached the start of a year, the share who remain through its end. Hover to see how many tenancies support each point."
        >
          <Line data={survivalChart.data} options={survivalChart.options} />
        </ChartCard>

        <ChartCard
          title="Tenants still in place by month"
          subtitle="Estimated share still in place at each month. The solid line uses reliable recorded history; the dashed line projects later years while preserving the renewal pattern."
        >
          <Line data={monthlyCumulativeChart.data} options={monthlyCumulativeChart.options} />
        </ChartCard>

        <ChartCard
          title="Monthly move-out rate"
          subtitle="Share moving out in each month of a tenancy. Red is what happened; the orange dashed line is the fitted model, drawn over the recorded months too so you can see how well it tracks. Both lines are 7-month averages. Hover for counts."
        >
          <Line data={monthlyChurnChart.data} options={monthlyChurnChart.options} />
        </ChartCard>

        <ChartCard
          title="How long tenancies last"
          subtitle="Blue estimates the final length of each tenancy. Grey shows time recorded so far, so active tenancies will eventually move into longer bands."
        >
          <Bar data={distributionChart.data} options={distributionChart.options} />
        </ChartCard>

        <ChartCard
          title="Move-out seasonality"
          subtitle="Move-out rate by calendar month, adjusted for the number of occupied units. A score of 100 is an average month; 120 means about 20% above average."
        >
          <Bar data={moveOutChart.data} options={moveOutChart.options} />
        </ChartCard>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b">
            <h3 className="text-base font-semibold text-gray-900">Retention detail</h3>
            <p className="text-sm text-gray-500 mt-0.5">
            The first two columns show the share reaching each anniversary. The last two show the
            share staying for that particular year. “All observed” can include inherited tenants;
            “recorded from move-in” does not. Grey rows have a small sample, so treat them cautiously.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium">Milestone</th>
                <th className="px-4 py-2 font-medium text-right">
                  Still there — all observed
                  <div className="text-xs font-normal text-gray-400">includes inherited tenants</div>
                </th>
                <th className="px-4 py-2 font-medium text-right">
                  Still there — full history
                  <div className="text-xs font-normal text-gray-400">recorded from move-in</div>
                </th>
                <th className="px-4 py-2 font-medium text-right border-l">
                  Stayed that year — all observed
                  <div className="text-xs font-normal text-gray-400">includes inherited tenants</div>
                </th>
                <th className="px-4 py-2 font-medium text-right">
                  Stayed that year — full history
                  <div className="text-xs font-normal text-gray-400">recorded from move-in</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const years = new Set();
                (data?.monthly_survival?.cumulative_at_year || []).forEach((c) => {
                  if (c.rate != null && (c.at_risk || 0) > 0) years.add(c.years);
                });
                (data?.monthly_survival?.annualized_by_year || []).forEach((a) => {
                  if (a.rate != null && (a.min_at_risk || 0) > 0) years.add(a.year);
                });
                (data?.year_retention || []).forEach((r) => {
                  if (r.rate != null && (r.eligible || 0) > 0) years.add(r.years);
                });
                (data?.survival_transitions || []).forEach((t) => {
                  if (t.rate != null && (t.at_risk || 0) > 0) years.add(t.to_year);
                });
                const withN = (rate, n) =>
                  rate == null ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    <>
                      {formatPct(rate)}
                      <span className="text-gray-400 ml-1 text-xs">n={n}</span>
                    </>
                  );
                return [...years]
                  .sort((a, b) => a - b)
                  .map((year) => {
                    const cum = (data?.monthly_survival?.cumulative_at_year || []).find(
                      (c) => c.years === year
                    );
                    const calendar = (data?.year_retention || []).find(
                      (r) => r.years === year
                    );
                    const band = (data?.monthly_survival?.annualized_by_year || []).find(
                      (r) => r.year === year && r.rate != null
                    );
                    const yoy = (data?.survival_transitions || []).find(
                      (r) => r.to_year === year
                    );
                    // Cumulative dies after a mid-curve gap; late conditional
                    // years can still be informative (Apex delayed entry).
                    const thin =
                      (cum && !cum.reliable) ||
                      (band && !band.reliable) ||
                      (cum?.rate == null && band?.rate != null);
                    return (
                      <tr
                        key={year}
                        className={`border-t ${thin ? 'text-gray-500' : ''}`}
                      >
                        <td className="px-4 py-2">
                          ≥{year} year{year === 1 ? '' : 's'}
                          {thin ? (
                            <span className="text-xs text-gray-400 ml-1">small sample</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">
                          {withN(cum?.rate, cum?.at_risk)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {withN(calendar?.rate, calendar?.eligible)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums border-l font-medium">
                          {withN(band?.rate, band?.min_at_risk)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {withN(yoy?.rate, yoy?.at_risk)}
                        </td>
                      </tr>
                    );
                  });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      <ChartCard
        title="Tenants grouped by move-in year"
        subtitle="Anniversary percentages appear only when a group has enough time to reach them. The orange dashed line shows the share still living there today."
      >
        <Bar data={cohortChart.data} options={cohortChart.options} />
      </ChartCard>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-900">Move-in year detail</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Anniversary percentages use only tenancies old enough to reach that point. “—” means
            the group is too recent to measure yet.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium">Start year</th>
                <th className="px-4 py-2 font-medium text-right">Tenants</th>
                <th className="px-4 py-2 font-medium text-right">Still current</th>
                <th className="px-4 py-2 font-medium text-right">≥1 yr</th>
                <th className="px-4 py-2 font-medium text-right">≥2 yr</th>
                <th className="px-4 py-2 font-medium text-right">≥3 yr</th>
                <th className="px-4 py-2 font-medium text-right">Observed tenure</th>
              </tr>
            </thead>
            <tbody>
              {(data?.cohorts_by_start_year || []).map((row) => (
                <tr key={row.year} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-900">{row.year}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.tenants}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatPct(row.still_current_rate)}
                    <span className="text-gray-400 ml-1">
                      ({row.still_current}/{row.tenants})
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatPct(row.retention_1yr)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatPct(row.retention_2yr)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {formatPct(row.retention_3yr)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatYears(row.avg_tenure_years)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-900">By owner</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Move-outs per occupied unit-year counts only the time we have owned each property, so
            owners bought at different times compare fairly. The ranges show how uncertain each
            figure is, especially for owners with fewer tenancies.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium">Owner</th>
                <th className="px-4 py-2 font-medium text-right">Tenants</th>
                <th className="px-4 py-2 font-medium text-right">Current</th>
                <th className="px-4 py-2 font-medium text-right">Move-outs</th>
                <th className="px-4 py-2 font-medium text-right">Occupied unit-years</th>
                <th className="px-4 py-2 font-medium text-right">Move-outs per unit-year</th>
                <th className="px-4 py-2 font-medium text-right">95% interval</th>
                <th className="px-4 py-2 font-medium text-right">1-yr retention</th>
                <th className="px-4 py-2 font-medium text-right">95% interval</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_owner || []).map((row) => (
                <tr key={row.owner} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-900">{row.owner}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.tenants}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.current_tenants}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.move_outs}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {row.occupied_unit_years == null ? '—' : Number(row.occupied_unit_years).toFixed(1)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {formatPct(row.move_out_rate)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {row.move_out_rate_ci_low == null
                      ? '—'
                      : `${formatPct(row.move_out_rate_ci_low)} – ${formatPct(
                          row.move_out_rate_ci_high
                        )}`}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatPct(row.retention_1yr)}
                    <span className="text-gray-400 ml-1">
                      (n={row.retention_1yr_eligible ?? 0})
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {row.retention_1yr_ci_low == null
                      ? '—'
                      : `${formatPct(row.retention_1yr_ci_low)} – ${formatPct(
                          row.retention_1yr_ci_high
                        )}`}
                  </td>
                </tr>
              ))}
              {!data?.by_owner?.length ? (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-gray-500">
                    No tenant data
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-900">Chance of staying one more year</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Share staying through each year among tenants who reached its start. Inherited tenants
            count only during the period we observed them. Grey rows have a small sample.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium">Transition</th>
                <th className="px-4 py-2 font-medium text-right">At risk</th>
                <th className="px-4 py-2 font-medium text-right">Rate</th>
              </tr>
            </thead>
            <tbody>
              {(data?.monthly_survival?.annualized_by_year || [])
                .filter((row) => row.rate != null && (row.min_at_risk || 0) > 0)
                .map((row) => {
                  const thin = !row.reliable;
                  return (
                    <tr
                      key={row.year}
                      className={`border-t ${thin ? 'text-gray-500' : ''}`}
                    >
                      <td className="px-4 py-2">
                        {row.year - 1}→{row.year} years
                        {thin ? (
                          <span className="text-xs text-gray-400 ml-1">small sample</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {row.min_at_risk}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {formatPct(row.rate)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {turnover ? (
        <>
          <div className="border-t pt-6 mt-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Turnover and capital reserves
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-4xl">
              Expected unit turns based on the same tenant-lifetime estimate above and the current
              age of each tenancy. Built from {turnover.occupancies} unit occupancies and{' '}
              {turnover.observed_exits} recorded move-outs. Multiple tenants in one unit count as
              one occupancy. These portfolio figures do not use the owner filter above.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <StatCard
              label="Next 12 months"
              value={formatTurns(turnover.portfolio?.turnover_path?.[0])}
              sub="Expected turns per unit from today's tenants"
            />
            <StatCard
              label="Long-run annual average"
              value={formatTurns(turnover.steady_state?.turns_per_unit_year)}
              sub="Expected turns per unit in a mature portfolio"
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <ChartCard
              title="Reserve schedule"
              subtitle="Expected turns per unit in each future year, based on how long current tenants have already stayed. The dashed line is the long-run annual average."
            >
              <Line data={reservePathChart.data} options={reservePathChart.options} />
            </ChartCard>
          </div>

        </>
      ) : null}
    </div>
  );
}

export default LeaseAnalysis;
